"use client";

import Image from "next/image";
import { useRef } from "react";
import { ChevronDown, Menu, User } from "lucide-react";
import { useDragScroll } from "@/hooks/use-drag-scroll";
import { Link } from "@/i18n/routing";
import { ScrollEdgeArrows, useScrollEdges } from "@/components/shared/main/scroll-edges";
import { primaryNav } from "@/fixtures/shared/nav/nav-v2-fixtures";
import { LanguageAction, NAV_ICON_BUTTON, SearchAction } from "./nav-actions";

type StickyNavProps = {
  visible: boolean;
  moreOpen: boolean;
  onToggleMore: () => void;
  /** Opens the account drawer — the same one the full masthead opens. */
  onOpenAccount: () => void;
  accountOpen: boolean;
};

export function StickyNav({
  visible,
  moreOpen,
  onToggleMore,
  onOpenAccount,
  accountOpen,
}: StickyNavProps) {
  /*
    Between 1280 and 1440 the row does not fit.

    Measured on the built page: the fifteen sections need 976px, and the row has
    825px at 1280 and 911px at 1366 — so three items were clipped at 1280 and
    two at 1366, and the ones that fell off the end were 信報視頻 and **更多**.
    Losing 更多 is the part that matters: it is the way to everything the bar
    cannot show, and it was the first thing to go.

    Two changes, no items removed. 更多 is lifted OUT of the scroller so it is
    always on screen at the end of the row, and the sections themselves get the
    same ‹ / › edge affordances the topic rows in this nav already use — each
    arrow existing only while there is something in that direction. Below `lg`
    the row is hidden entirely and the hamburger carries the whole menu, which
    is unchanged.
  */
  const listRef = useRef<HTMLUListElement>(null);
  const edges = useScrollEdges<HTMLUListElement>(listRef);
  /* Grab-and-drag as well as the arrows: at 1280–1440 the row is a few items
     short of fitting, and reaching for a 24px chevron to move it 150px is more
     work than pushing the row itself. The shared hook — mouse only, touch keeps
     native scrolling — swallows the click that ends a drag, so dragging past a
     section does not open it. QA: "for smaller desktop size, the navbar items
     should be horizontally draggable by cursor". */
  const drag = useDragScroll<HTMLUListElement>(listRef);
  return (
    <div
      className="hkej-sticky-nav fixed inset-x-0 top-0 z-[60] w-full transition-transform duration-200 ease-out"
      style={{
        transform: visible ? "translateY(0)" : "translateY(-100%)",
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
      aria-hidden={!visible}
    >
      <div className="hkej-sticky-nav-bar mx-auto w-full max-w-[1440px] px-4 lg:px-[48px]">
        <div className="flex items-center" style={{ gap: 10, height: 56 }}>
          <button
            type="button"
            aria-label="選單"
            onClick={onToggleMore}
            className="flex shrink-0 items-center justify-center lg:hidden"
          >
            <Menu
              style={{ width: 24, height: 24, color: "#1d1d1f" }}
              strokeWidth={1.8}
            />
          </button>

          <Link
            href="/"
            aria-label="信報"
            className="relative shrink-0"
            style={{ width: 130, height: 44 }}
          >
            <Image
              src="/images/nav-v2/hkej-logo-v2.png"
              alt="信報"
              fill
              className="object-contain object-left"
              sizes="140px"
            />
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center lg:flex">
            {/*
              `shrink` and NOT `flex-1`.

              Growing to fill the row pushed 更多 to the far right of the
              available space, so at widths where everything fits there was a
              gap of empty scroller between the last section and it. QA: "remove
              the gap between the more button and the navbar items". The
              scroller is its content's width and only shrinks when the row
              runs out of room.
            */}
            <div className="relative min-w-0 shrink">
            <ul
              ref={listRef}
              {...drag.dragProps}
              className={`flex items-center overflow-x-auto no-scrollbar ${drag.dragClass}`}
              style={{
                fontSize: 14,
                lineHeight: "16.8px",
                gap: 14,
              }}
            >
              {primaryNav.map((item) => (
                <li key={item.label} className="shrink-0">
                  <Link
                    href={item.href}
                    className="whitespace-nowrap text-black hover:text-[#0e8f63]"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
              <ScrollEdgeArrows scroller={listRef} edges={edges} />
            </div>

            {/* Outside the scroller, so the way to everything the row cannot
                show is never the thing that scrolls off it. */}
            <span
              className="ml-[8px] shrink-0 font-bold"
              style={{ color: "#096344", fontSize: 14, lineHeight: "16.8px" }}
              aria-hidden="true"
            >
              -
            </span>
            <button
              type="button"
              onClick={onToggleMore}
              aria-expanded={moreOpen}
              className="ml-[8px] inline-flex shrink-0 items-center font-bold"
              style={{ color: "#0e8f63", gap: 4, fontSize: 14, lineHeight: "16.8px" }}
            >
              <span>更多</span>
              <ChevronDown
                style={{
                  width: 16,
                  height: 16,
                  transform: moreOpen ? "rotate(180deg)" : undefined,
                  transition: "transform 150ms",
                }}
                strokeWidth={2}
              />
            </button>
          </nav>

          <div
            className="ml-auto flex shrink-0 items-center"
            style={{ gap: 16 }}
          >
            <SearchAction iconSize={22} />
            {/* Not on a phone: the condensed bar there carries a hamburger, the
                masthead, search, the account and 立即訂閱 in 390px, and the
                language switch is the one control that is also in the menu the
                hamburger opens. QA: "for mobile size scrolled navbar: remove
                the locale icon button". */}
            <span className="hidden sm:inline-flex">
              <LanguageAction iconSize={22} panelAlign="right" />
            </span>
            {/* The condensed bar carried search, language and 立即訂閱 but no
                way to the account — so scrolling took the control away and the
                reader had to go back to the top to reach it. QA: "for the
                scrolled navbar, add the account icon button". Same drawer as
                the full masthead's, so there is one account surface. */}
            <button
              type="button"
              aria-label="會員"
              aria-haspopup="dialog"
              aria-expanded={accountOpen}
              onClick={onOpenAccount}
              className={`${NAV_ICON_BUTTON} rounded-full`}
              style={{ width: 22, height: 22 }}
            >
              <User style={{ width: 22, height: 22 }} strokeWidth={1.8} />
            </button>
            <Link
              href="/subscription"
              className="inline-flex items-center justify-center font-bold"
              style={{
                backgroundColor: "#56917d",
                color: "#ffffff",
                padding: "7px 12px",
                borderRadius: 2,
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              立即訂閱
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
